import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { AuthError, Provider, Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface AuthState { user: User | null; session: Session | null; loading: boolean }
interface AuthResult { error: AuthError | null }
interface UseAuthReturn extends AuthState {
  signUp: (email: string, password: string) => Promise<AuthResult & { user: User | null; session: Session | null }>
  signIn: (email: string, password: string) => Promise<AuthResult & { user: User | null }>
  signInWithOAuth: (provider: Extract<Provider, 'google' | 'github'>, returnTo?: string) => Promise<AuthResult>
  signInWithGoogle: (returnTo?: string) => Promise<AuthResult>
  signInWithGitHub: (returnTo?: string) => Promise<AuthResult>
  signOut: () => Promise<AuthResult>
  resetPassword: (email: string) => Promise<AuthResult>
  updatePassword: (newPassword: string) => Promise<AuthResult>
}

const AuthContext = createContext<UseAuthReturn | null>(null)
const safeReturnPath = (path?: string) => !path || !path.startsWith('/') || path.startsWith('//') ? '/my-diagrams' : path

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({ user: null, session: null, loading: true })

  useEffect(() => {
    let active = true
    void supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!active) return
      if (error) console.error('Unable to restore authentication session:', error)
      setAuthState({ user: session?.user ?? null, session: session ?? null, loading: false })
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (active) setAuthState({ user: session?.user ?? null, session: session ?? null, loading: false })
    })
    return () => { active = false; subscription.unsubscribe() }
  }, [])

  const signUp = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    return { user: data.user, session: data.session, error }
  }, [])
  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { user: data.user, error }
  }, [])
  const signInWithOAuth = useCallback(async (provider: Extract<Provider, 'google' | 'github'>, returnTo?: string) => {
    const redirect = new URL('/auth/callback', window.location.origin)
    redirect.searchParams.set('returnTo', safeReturnPath(returnTo))
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: redirect.toString() } })
    return { error }
  }, [])
  const signInWithGoogle = useCallback((returnTo?: string) => signInWithOAuth('google', returnTo), [signInWithOAuth])
  const signInWithGitHub = useCallback((returnTo?: string) => signInWithOAuth('github', returnTo), [signInWithOAuth])
  const signOut = useCallback(async () => { const { error } = await supabase.auth.signOut(); return { error } }, [])
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` })
    return { error }
  }, [])
  const updatePassword = useCallback(async (password: string) => { const { error } = await supabase.auth.updateUser({ password }); return { error } }, [])

  const value = useMemo<UseAuthReturn>(() => ({ ...authState, signUp, signIn, signInWithOAuth, signInWithGoogle, signInWithGitHub, signOut, resetPassword, updatePassword }), [authState, resetPassword, signIn, signInWithGitHub, signInWithGoogle, signInWithOAuth, signOut, signUp, updatePassword])
  return React.createElement(AuthContext.Provider, { value }, children)
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
export const useIsAuthenticated = () => { const { user, loading } = useAuth(); return !loading && !!user }
export const useRequireAuth = (redirectTo = '/') => { const { user, loading } = useAuth(); return { user, loading, shouldRedirect: !loading && !user, redirectTo } }
